import React, { useEffect, useState } from "react";
import RIcon from "../../reservas/components/ReservasIcons";
import ReviewsSection from "../../reservas/components/ReviewsSection";
import { SedeSwap, SucursalesSection } from "../../reservas/components/Sucursales";
import {
  ContactSection,
  DescriptionSection,
  GallerySection,
  HoursSection,
  LocationSection,
  PoliciesSection,
  ProfessionalsSection,
  ServicesSection,
  getSalonSections,
  sectionId,
} from "./SalonSections";

export const SalonEmptyServices = ({ onBack }) => (
  <div className="empty-state">
    <RIcon name="spark" width={48} height={48} />
    <h3>Este local todavía no tiene servicios para reservar</h3>
    <p>Volvé más tarde o mirá otras reservas disponibles.</p>
    <button type="button" className="r-btn-primary" onClick={onBack}>
      Ver otras reservas
    </button>
  </div>
);

/**
 * Sticky text tabs of the desktop info view (same `info-tabs-sticky` markup as
 * Canchas). The active tab follows the scroll through an IntersectionObserver.
 */
const InfoTabs = ({ items }) => {
  const [active, setActive] = useState(items[0]?.key);
  const keys = items.map((item) => item.key).join("|");

  useEffect(() => {
    if (typeof window === "undefined" || !window.IntersectionObserver) return undefined;
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id.replace(/^beauty-/, ""));
        });
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: 0 }
    );
    keys.split("|").forEach((key) => {
      const el = document.getElementById(sectionId(key));
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [keys]);

  const go = (key) => {
    setActive(key);
    const el = document.getElementById(sectionId(key));
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <div className="info-tabs-sticky">
      {items.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          className={`info-tab-link${active === key ? " active" : ""}`}
          onClick={() => go(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

/**
 * Body of the salon page shared by the desktop and mobile layouts: the info
 * tabs (sticky on both layouts) plus every section that has content. Desktop passes the
 * cart handlers (service cards get "+ Agregar"); mobile leaves them out.
 */
const SalonContent = ({
  salon,
  services,
  professionals,
  isDesktop = false,
  cartIds,
  onAdd,
  onEdit,
  onRemove,
  onBack,
  sucursales = [],
  sedes = null,
  sedeStatus = null,
}) => {
  const pending = Boolean(sedeStatus);
  const { description, hasHours, hasContact, gallery, hasLocation, nav } = getSalonSections({
    salon,
    services: pending ? [] : services,
    professionals: pending ? [] : professionals,
    sucursales,
  });
  // Beauty casa central: the bookable sections below belong to the chosen sede.
  const inSede = (title) => (sedes ? `${title} en ${salon.name}` : title);
  const sucursalesBlock = (
    <SucursalesSection kind="beauty" sucursales={sucursales} id={sectionId("sucursales")} isDesktop={isDesktop} sedes={sedes} />
  );

  if (pending) {
    // The swapped area while the chosen sede's detail loads or failed; the Sucursales section stays.
    return (
      <>
        {nav.length > 0 && <InfoTabs items={nav.filter((n) => n.key === "sucursales")} />}
        {sucursalesBlock}
        <SedeSwap status={sedeStatus} />
      </>
    );
  }

  return (
    <>
      {nav.length > 0 && <InfoTabs items={nav} />}
      {sedes && sucursalesBlock}
      {services.length > 0 ? (
        <ServicesSection
          title={inSede("Servicios")}
          services={services}
          cartIds={cartIds}
          onAdd={onAdd}
          onEdit={onEdit}
          onRemove={onRemove}
          isDesktop={isDesktop}
        />
      ) : (
        <SalonEmptyServices onBack={onBack} />
      )}
      {!sedes && sucursalesBlock}
      {professionals.length > 0 && <ProfessionalsSection title={inSede("Profesionales")} professionals={professionals} isDesktop={isDesktop} />}
      {description && <DescriptionSection text={description} isDesktop={isDesktop} />}
      {gallery.length > 0 && <GallerySection images={gallery} name={salon.name} isDesktop={isDesktop} />}
      {hasLocation && <LocationSection salon={salon} isDesktop={isDesktop} />}
      {hasHours && <HoursSection openingHours={salon.openingHours} isDesktop={isDesktop} />}
      {hasContact && <ContactSection salon={salon} isDesktop={isDesktop} />}
      <PoliciesSection salon={salon} isDesktop={isDesktop} />
      <ReviewsSection barId={salon._id} id={sectionId("resenas")} isDesktop={isDesktop} />
    </>
  );
};

export default SalonContent;
